import { fromEvent as observableFromEvent, Observable, Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { map, pairwise, filter, debounceTime, tap } from 'rxjs/operators';
import { ChatLoggingService } from './chat-logging.service';

interface ScrollPosition {
  sH: number;
  sT: number;
  cH: number;
  target: any;
}

/*
const DEFAULT_SCROLL_POSITION: ScrollPosition = {
  sH: 0,
  sT: 0,
  cH: 0,
  target: null,
};*/

@Injectable({
  providedIn: 'root'
})
export class ScrollingService {
  scrollingContainer: Element;
  private scrollEvent$;
  private userScrolledUp$;
  private userScrolledDown$;
  scrollPercent = 5;
  private subs: Subscription = new Subscription();
  public isScrolling = false;
  private scrollCount = 0;
  public checkForSpaceFromBottom: boolean = true;
  public spaceFromBottom: number = 0;
  public previousSpaceFromBottom: number = 0;
  public previousSpaceFromBottomTimeout: any;
  public lastScrolledTo: {
    messageId: string;
    scrollTop: number;
  } = {
    messageId: null,
    scrollTop: null
  };

  constructor(private chatLoggingService: ChatLoggingService) {}

  setContainer(element: Element) {
    if (element) {
      this.scrollingContainer = element;
      this.registerScrollEvent();
      this.streamScrollEvents();
    }
  }

  getScrollObservable(): Observable<any> {
    return this.scrollEvent$.pipe(debounceTime(100));
  }

  getScrollUpObservable(): Observable<any> {
    return this.userScrolledUp$;
  }

  getScrollDownObservable(): Observable<any> {
    return this.userScrolledDown$;
  }

  private registerScrollEvent() {
    this.scrollEvent$ = observableFromEvent(this.scrollingContainer, 'scroll');
  }

  private streamScrollEvents() {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.subs.add(
      this.scrollEvent$
        .pipe(
          tap(() => {
            if (this.checkForSpaceFromBottom) {
              this.previousSpaceFromBottom = this.spaceFromBottom;
              if (this.previousSpaceFromBottomTimeout) {
                clearTimeout(this.previousSpaceFromBottomTimeout);
                this.previousSpaceFromBottomTimeout = null;
              }
              this.previousSpaceFromBottomTimeout = setTimeout(() => {
                this.previousSpaceFromBottom = null;
                this.chatLoggingService.debug({
                  logMessage: 'scrolling.service.streamScrollEvents(): Clear previous space from bottom'
                });
              }, 1000);
              this.spaceFromBottom =
                this.scrollingContainer.scrollHeight -
                this.scrollingContainer.scrollTop -
                this.scrollingContainer.clientHeight;
              this.chatLoggingService.debug({
                logMessage: 'scrolling.service.streamScrollEvents(): Space from bottom',
                logParameters: [this.spaceFromBottom]
              });
            }
            this.isScrolling = true;
            this.scrollCount++;
            if (this.scrollCount > 4) {
              this.lastScrolledTo = {
                messageId: null,
                scrollTop: null
              };
            }
          }),
          debounceTime(500)
        )
        .subscribe(event => {
          this.isScrolling = false;
          this.scrollCount = 0;
        })
    );

    this.userScrolledUp$ = this.scrollEvent$.pipe(
      map(
        (e: any): ScrollPosition => ({
          sH: e.target.scrollHeight,
          sT: e.target.scrollTop,
          cH: e.target.clientHeight,
          target: e.target
        })
      ),
      pairwise(),
      filter(positions => {
        if (this.isUserScrollingUp(positions) && this.isScrollUpExpectedPercent(positions[1])) {
          return true;
        } else {
          return false;
        }
      })
    );

    this.userScrolledDown$ = this.scrollEvent$.pipe(
      map(
        (e: any): ScrollPosition => ({
          sH: e.target.scrollHeight,
          sT: e.target.scrollTop,
          cH: e.target.clientHeight,
          target: e.target
        })
      ),
      pairwise(),
      filter(positions => {
        if (this.isUserScrollingDown(positions) && this.isScrollDownExpectedPercent(positions[1])) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  private isUserScrollingUp = positions => {
    return positions[0].sT > positions[1].sT && positions[0].sH === positions[1].sH;
  };

  private isUserScrollingDown = positions => {
    return positions[0].sT < positions[1].sT && positions[0].sH === positions[1].sH;
  };

  private isScrollUpExpectedPercent = position => {
    return position.sT / position.sH < this.scrollPercent / 100 || position.sT === 0;
  };

  private isScrollDownExpectedPercent = position => {
    return (
      (position.sH - position.sT - position.cH) / position.sH < this.scrollPercent / 100 ||
      position.sH - position.sT - position.cH === 0
    );
  };

  isScrollFullHeight(): boolean {
    let height: number = this.scrollingContainer.scrollHeight;
    let clientHeight: number = this.scrollingContainer.clientHeight;
    //fix for extra fetchMessages requests to fill the height of the page in MiCollab app
    if (height === 0 && clientHeight === 0) {
      this.chatLoggingService.debug({
        logMessage: 'scrolling.service.isScrollFullHeight() - height and clientHeight of the container are 0'
      });
      return false;
    }
    let diff: number = height - clientHeight;
    let ret = diff >= 0 && diff < 10;
    this.chatLoggingService.debug({
      logMessage: 'scrolling.service.isScrollFullHeight()',
      logParameters: [height, clientHeight, diff, ret]
    });
    return ret;
  }

  adjustScrollTopForMore(previousScrollHeight: number) {
    if (!this.scrollingContainer || !previousScrollHeight) return;
    if (this.scrollingContainer.scrollHeight - previousScrollHeight >= 50) {
      this.scrollingContainer.scrollTop = this.scrollingContainer.scrollHeight - previousScrollHeight;
    } else {
      this.scrollingContainer.scrollTop = 50;
    }
  }

  isNearBottom(): boolean {
    let height: number = Math.ceil(this.scrollingContainer.scrollHeight - this.scrollingContainer.scrollTop);
    let clientHeight: number = this.scrollingContainer.clientHeight;
    let diff: number = height - clientHeight;
    let ret = diff >= 0 && diff < 100;
    return ret;
  }

  scrollToBottom() {
    if (!this.scrollingContainer) return;

    this.chatLoggingService.debug({
      logMessage: 'scrolling.service.scrollToBottom() scroll and client heights',
      logParameters: [this.scrollingContainer.scrollHeight, this.scrollingContainer.clientHeight]
    });

    this.scrollingContainer.scrollTop = this.scrollingContainer.scrollHeight;
    this.spaceFromBottom = 0;
    this.previousSpaceFromBottom = null;
  }

  scrollToMessage(messageId: string) {
    if (messageId) {
      this.chatLoggingService.info({
        logMessage: 'scrolling.service.scrollToMessage()',
        logParameters: [messageId]
      });
      let msgElement = document.getElementById(messageId);
      if (msgElement) {
        this.scrollCount = 0;
        //Needed to fix race condition so the resize sensor doesn't scroll somewhere else
        this.spaceFromBottom = null;
        this.scrollingContainer.scrollTop = msgElement.offsetTop;
        this.lastScrolledTo = {
          messageId: messageId,
          scrollTop: this.scrollingContainer.scrollTop
        };
      } else {
        this.chatLoggingService.info({
          logMessage: 'scrolling.service.scrollToMessage()=> no message element',
          logParameters: [messageId]
        });
      }
    }
  }

  scrollPage(pageHeight: number, down: boolean = true) {
    if (!down) {
      if (this.scrollingContainer.scrollTop - pageHeight < 0) {
        this.scrollingContainer.scrollTop = 0;
      } else {
        this.scrollingContainer.scrollTop -= pageHeight;
      }
    } else {
      if (this.scrollingContainer.scrollTop + pageHeight > this.scrollingContainer.scrollHeight) {
        this.scrollingContainer.scrollTop = this.scrollingContainer.scrollHeight;
      } else {
        this.scrollingContainer.scrollTop += pageHeight;
      }
    }
  }
}
